import {
    Box,
    Text,
    Flex,
    HStack,
    Spinner,
    Center,
} from '@chakra-ui/react';
import React from 'react';
import AppDrawer from '../../components/AppDrawer';
import { Icon, IconButton } from '../../ui';
import { useInventoryExportHistory } from '../../hooks/inventory/useExportInventoryHistory';
import { INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../sideDrawerContainerProps';
import type { InventoryExportHistoryEntry } from '../../types/inventory';

function formatExportMode(mode: InventoryExportHistoryEntry['mode']): string {
    return mode === 'all' ? 'All' : 'Filtered';
}

interface InventoryExportHistoryDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    highlightExportId?: string | null;
    /** When set, shows a Back button in the header (e.g. return to Inventory Options). */
    onBack?: () => void;
    /** Also skip the overlay fade (sub-page swap with Inventory Options). */
    instantMotion?: boolean;
}

const InventoryExportHistoryDrawer: React.FC<InventoryExportHistoryDrawerProps> = ({
    isOpen,
    onClose,
    highlightExportId,
    onBack,
    instantMotion,
}) => {
    const { data, isLoading, isError } = useInventoryExportHistory(isOpen);
    const exports = data?.exports ?? [];

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            title="Export History"
            titleAddon={
                onBack ? (
                    <IconButton
                        aria-label="Back"
                        icon={<Icon name="arrowLeft" size="sm" />}
                        variant="ghost"
                        colorScheme="gray"
                        size="sm"
                        ml={-2}
                        onClick={onBack}
                    />
                ) : undefined
            }
            instantMotion={instantMotion}
            contentProps={{ motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS }}
        >
            {isLoading && (
                <Center py={12}>
                    <Spinner color="#0071E3" />
                </Center>
            )}
            {isError && (
                <Text fontSize="sm" color="red.500" textAlign="center" py={8}>
                    Failed to load export history
                </Text>
            )}
            {!isLoading && !isError && exports.length === 0 && (
                <Text
                    fontSize="sm"
                    color="gray.400"
                    fontStyle="italic"
                    textAlign="center"
                    py={8}
                >
                    No exports yet
                </Text>
            )}
            {!isLoading && !isError && exports.length > 0 && (
                <Flex direction="column" gap={2}>
                    {exports.map((entry) => {
                        const isHighlighted = highlightExportId === entry.exportId;
                        return (
                            <Box
                                key={entry.exportId}
                                borderWidth="1px"
                                borderColor={isHighlighted ? '#0071E3' : 'gray.200'}
                                borderRadius="lg"
                                bg={isHighlighted ? '#0071E30D' : 'white'}
                                p={3}
                            >
                                <Flex align="center" justify="space-between" gap={2}>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                        {formatExportMode(entry.mode)} Export
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" flexShrink={0}>
                                        {new Date(entry.createdAt).toLocaleString()}
                                    </Text>
                                </Flex>
                                {entry.userName ? (
                                    <Text fontSize="xs" color="gray.600" mt={0.5}>
                                        {entry.userName}
                                    </Text>
                                ) : null}
                                <HStack spacing={1.5} mt={1}>
                                    <Text fontSize="xs" color="gray.600">
                                        {entry.rowCount} row{entry.rowCount === 1 ? '' : 's'}
                                    </Text>
                                    {entry.filename && (
                                        <>
                                            <Text fontSize="xs" color="gray.400">
                                                ·
                                            </Text>
                                            <Text fontSize="xs" color="gray.600" isTruncated>
                                                {entry.filename}
                                            </Text>
                                        </>
                                    )}
                                </HStack>
                            </Box>
                        );
                    })}
                </Flex>
            )}
        </AppDrawer>
    );
};

export default InventoryExportHistoryDrawer;
